import { PORTAL_APEX_DOMAIN } from "./portal/portal_host";

/** Wildcard HTTPS origins for local dev tunnels. Better Auth `trustedOrigins`. */
export const DEV_TUNNEL_TRUSTED_ORIGIN_WILDCARDS: readonly string[] = [
  "https://*.ngrok-free.app",
  "https://*.ngrok.io",
  "https://*.ngrok.app",
  "https://*.trycloudflare.com",
];

const DEV_TUNNEL_HOST_SUFFIXES: readonly string[] = [
  ".ngrok-free.app",
  ".ngrok.io",
  ".ngrok.app",
  ".trycloudflare.com",
];

/**
 * True on the Convex production deployment (Convex sets NODE_ENV="production"
 * there; mirrors the existing check in convex/email/outbound/compose.ts). Used
 * to keep attacker-registrable dev-tunnel origins (*.ngrok-free.app etc.) OUT
 * of Better Auth `trustedOrigins` in production, where a trusted origin can be
 * abused as a callbackURL/redirect target.
 */
function isProductionRuntime(): boolean {
  return process.env.NODE_ENV === "production";
}

/** True when the page is served through a local HTTPS dev tunnel (ngrok, Cloudflare). */
export function isDevTunnelHostname(hostname: string): boolean {
  const host = hostname.trim().toLowerCase();
  return DEV_TUNNEL_HOST_SUFFIXES.some((suffix) => host.endsWith(suffix));
}

/** True for https origins on ngrok / trycloudflare dev tunnels. */
export function isDevTunnelOrigin(origin: string): boolean {
  const trimmed = origin.trim();
  if (!trimmed) return false;
  try {
    const url = new URL(trimmed);
    return url.protocol === "https:" && isDevTunnelHostname(url.hostname);
  } catch {
    return false;
  }
}

/** @deprecated Use isDevTunnelHostname */
export function isNgrokTunnelHostname(hostname: string): boolean {
  return isDevTunnelHostname(hostname);
}

/** Origins allowed for Better Auth (prod, localhost, ngrok, optional explicit tunnel URL). */
export function buildBetterAuthTrustedOrigins(siteUrl: string): readonly string[] {
  // Single source of truth — same apex derivation as the cookie-domain logic
  // (portal_host.ts), so trustedOrigins and the Set-Cookie Domain never diverge
  // on a custom apex (NEXT_PUBLIC_PORTAL_APEX_DOMAIN / PORTAL_APEX_DOMAIN).
  const portalApex = PORTAL_APEX_DOMAIN;
  const origins = new Set<string>([
    siteUrl.replace(/\/$/, ""),
    "https://hausverwalter.ai",
    "https://www.hausverwalter.ai",
    "https://app.hausverwalter.ai",
    "https://*.hausverwalter.ai",
    // Customer portal apex + every org subdomain (<slug>.portalzugang.de):
    // required so sign-in POSTs from a portal subdomain pass the origin check.
    `https://${portalApex}`,
    `https://*.${portalApex}`,
    "http://localhost:3000",
  ]);

  // Dev tunnels (ngrok / cloudflare) are free, attacker-registrable subdomains,
  // so they are trusted ONLY outside production. In prod they must never pass
  // the Better Auth origin / callbackURL check.
  if (!isProductionRuntime()) {
    for (const wildcard of DEV_TUNNEL_TRUSTED_ORIGIN_WILDCARDS) {
      origins.add(wildcard);
    }
    const tunnelOrigin = process.env.DEV_TUNNEL_ORIGIN?.trim().replace(
      /\/$/,
      "",
    );
    if (tunnelOrigin) origins.add(tunnelOrigin);
  }

  return [...origins];
}

/** Resolves trusted origins per request — allows the active dev tunnel origin explicitly. */
export function resolveBetterAuthTrustedOrigins(
  siteUrl: string,
  request: Request | undefined,
): readonly string[] {
  const origins = new Set<string>(buildBetterAuthTrustedOrigins(siteUrl));
  // Echo the request Origin only for dev tunnels AND only outside production —
  // in prod an inbound `Origin: https://<x>.ngrok-free.app` must not be trusted.
  if (!isProductionRuntime()) {
    const originHeader = request?.headers.get("origin")?.trim();
    if (originHeader && isDevTunnelOrigin(originHeader)) {
      origins.add(originHeader.replace(/\/$/, ""));
    }
  }
  return [...origins];
}
